import type { CameraId, EnvironmentId } from "@org-avp/avp-3dws-jetstream/dist/src/types";

/**
 * These Javascript properties are used by the web component.
 *
 * __Important Note:__
 *
 * All JavaScript properties are exposed as HTML attributes with a change in casing. JavaScript properties are defined in `camelCase`, whereas the corresponding HTML attributes are available in `kebab-case`.
 *
 * For example, the `configId` property can be used as an HTML attribute via `config-id`.
 */
export interface DeckHtmlAttributes {
  /**
   * The ID of a configuration to fetch to configure the app UI and the stream
   */
  "config-id": string;

  /**
   * The streaming stage to use, will default to the stage specified in the config if unset.
   */
  "streaming-stage"?: StreamingStage;

  /**
   * The car configuration of the streamed vehicle. Updating this will immediately take effect in the stream.
   */
  "pr-string"?: string;

  /**
   * Controls if the stream is playing or not
   */
  active?: boolean;

  /**
   * The market that the streaming connection should be initiated with.
   * If no market is provided here, instead the market in the cms config will be used.
   */
  market?: string;

  /**
   * For development purposes. In "dev" mode, the stream UI is shown even without the 3D stream running.
   */
  dev?: boolean;

  /**
   * Existing Session ID to connect the stream to
   */
  "session-id"?: string;

  /**
   * Provide camera id to load a specific camera
   */
  camera?: CameraId;

  /**
   * Controls the camera flight. Set to `"enabled"` to start the flight or `"disabled"` to stop it.
   * This takes precedence over what is set in the config for the `cameraFlightOnStart` property.
   */
  "camera-flight"?: "enabled" | "disabled";

  /**
   * Enables or disables the termination of a multi-session on disconnect.
   * Supposed be set on the host implementation side in order to terminate a 3D remote multi-session, once the host closes the stream.
   */
  "multisession-disconnect"?: boolean;

  /**
   * Disables viewing the car's consumption data (WLTP).
   * Can be disabled in use cases where consumption data will be displayed separately by the integrator.
   */
  "hide-consumption-data"?: boolean;

  /**
   * The car's consumption data (WLTP).
   * Will be passed to the AVE to display in-stream.
   *
   * **WARNING:**
   * Changing this attribute in an active stream will NOT trigger any immediate changes
   * since the AVE only updates the consumption data when the PR string changes!
   * To update the consumption data please update the PR string.
   */
  "consumption-data"?: string;

  /**
   * Enable or disable specific features by providing a stringified object of type {@link DeckFeatureFlags}.
   * This is a partial runtime override of the configuration via {@link config-id}.
   * Only the features specified in this attribute will be overridden.
   *
   * @example
   * ```
   * features="{\"zoom\":true}"
   * ```
   */
  features?: string;
}

/** @internal */
export interface DeckProperties {
  configId: DeckHtmlAttributes["config-id"];
  streamingStage?: DeckHtmlAttributes["streaming-stage"];
  prString?: DeckHtmlAttributes["pr-string"];
  active?: DeckHtmlAttributes["active"];
  market?: DeckHtmlAttributes["market"];
  dev?: DeckHtmlAttributes["dev"];
  sessionId?: DeckHtmlAttributes["session-id"];
  camera?: DeckHtmlAttributes["camera"];
  cameraFlight?: DeckHtmlAttributes["camera-flight"];
  multisessionDisconnect?: DeckHtmlAttributes["multisession-disconnect"];
  hideConsumptionData?: DeckHtmlAttributes["hide-consumption-data"];
  consumptionData?: DeckHtmlAttributes["consumption-data"];
  features?: DeckHtmlAttributes["features"];
}

/**
 * Informative event fired by the app.
 *
 * @emits [CustomEvent](https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent)
 * @see {@link DeckUpdateEvent}
 * @see {@link DeckConnectedEvent}
 * @see {@link DeckStreamInitializedEvent}
 * @see {@link DeckDisconnectedEvent}
 * @see {@link DeckExceptionEvent}
 */
export type DeckEvent =
  | DeckUpdateEvent
  | DeckConnectedEvent
  | DeckStreamInitializedEvent
  | DeckDisconnectedEvent
  | DeckExceptionEvent;

export type DeckDisconnectedEventReason =
  | "DISCONNECTED"
  | "MULTISESSION_DISCONNECTED"
  | "INACTIVITY_TIMEOUT";

export type DeckCustomErrorCode =
  | "UNKNOWN_EXCEPTION"
  | "AVE_CAR_NOT_INSTALLED"
  | "AVE_ENVIRONMENT_NOT_INSTALLED"
  | "NO_INSTANCES_AVAILABLE"
  | "PR_STRING_NOT_PROVIDED"
  | "CONFIGURATION_ERROR"
  | "BRAND_NOT_PROVIDED"
  | "BRAND_NOT_SUPPORTED"
  | "SERVICE_NOT_PROVIDED"
  | "MARKET_NOT_PROVIDED";

/**
 * Event that fires when a property is updated from within the app.
 *
 * @emits [CustomEvent](https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent)
 * of type `update:T` with detail `{ value: DeckHtmlAttributes[P] }`,
 * where `T` is the name of the {@link DeckHtmlAttributes | DeckHtmlAttribute} that was updated.
 *
 * @example
 * ```js
 * .addEventListener('update:active', (event) =>
 *   console.log("The 'active' attribute has been updated. Its new value is", event.detail.value)
 * )
 * ```
 */
export type DeckUpdateEvent<T extends keyof DeckHtmlAttributes = keyof DeckHtmlAttributes> =
  T extends any
    ? {
        type: `update:${T}`;
        detail: { value: DeckHtmlAttributes[T] };
      }
    : never;

/**
 * Event that fires when the stream is connected to the server and ready to receive commands.
 * The car and the environment are not yet loaded at this point.
 *
 * @emits [CustomEvent](https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent)
 * of type `connected` with detail `{ sessionId: DeckHtmlAttributes["session-id"] }`,
 * where `sessionId` is the ID of the 3D streaming session that has been established.
 *
 * @example
 * ```js
 * .addEventListener('connected', (event) =>
 *   console.log("The app has just connected to the stream and content has not been loaded yet. The ID of the streaming session is", event.detail.sessionId)
 * )
 * ```
 */
export type DeckConnectedEvent = {
  type: "connected";
  detail: { sessionId: DeckHtmlAttributes["session-id"] };
};

/**
 * Event that fires when the stream has loaded all the necessary assets, including the car and the environment.
 *
 * @emits [CustomEvent](https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent)
 * of type `streamInitialized`
 *
 * @example
 * ```js
 * .addEventListener('streamInitialized', (event) =>
 *   console.log("The stream finished initializing and the content finished loading.")
 * )
 * ```
 */
export type DeckStreamInitializedEvent = {
  type: "streamInitialized";
  detail: boolean;
};

/**
 * Event details contained in a {@link DeckEvent }
 */
export type DeckEventDetail = {
  reason: DeckCustomErrorCode;
  message?: string;
};

/**
 * Event that fires when an unrecoverable error occurred.
 *
 * @emits [CustomEvent](https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent)
 * of type `exception` with detail `{ code: DeckCustomErrorCode, reason: string }`
 * where `reason` is an optional custom error message.
 * Additionally, the event will
 *   * contain the originally thrown `error` object of type `Error | AggregateError`
 *   * implement properties of [ErrorEvent](https://developer.mozilla.org/en-US/docs/Web/API/ErrorEvent)
 *
 * @example
 * ```js
 * .addEventListener('exception', (event) =>
 *   console.log(`An unrecoverable error exception occurred. The error reason is ${event.detail.reason} with message: ${event.detail.message}`)
 * )
 * ```
 */
export type DeckExceptionEvent = {
  type: "exception";
  detail: DeckEventDetail;
  error: Error;
};

/**
 * Event that fires when the stream is disconnected.
 *
 * @emits [CustomEvent](https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent)
 * of type `disconnected` with detail `{ reason: DeckDisconnectedEventReason }`.
 *
 * @example
 * ```js
 * .addEventListener('disconnected', (event) =>
 *   console.log("The session was disconnected with reason", event.detail.reason)
 * )
 * ```
 */
export type DeckDisconnectedEvent = {
  type: "disconnected";
  detail: {
    reason: DeckDisconnectedEventReason;
  };
};

/**
 * The 3d web streaming stage to use for the stream.
 *
 * The value of `prelive` was deprecated in favor of `prelive-euw1`!
 */
export type StreamingStage =
  | "dev"
  | "qa"
  | "prelive"
  | "prelive-euc1"
  | "prelive-euw1"
  | "sandbox"
  | "prod-euc1"
  | "prod-euw1"
  | "prod-eun1"
  | "prod-use1"
  | "prod-usw2"
  | "prod-apse1";

/**
 * @internal
 * Configuration object that is fetched when component mounts
 */
export interface DeckConfig {
  /**
   * The brand to provide the stream of.
   */
  brand?: Brand;

  /**
   * The name of service to provide the stream for.
   */
  service?: string;

  /**
   * The market to provide the stream for as an alpha 3 country code.
   */
  market?: string;

  /**
   * The {@link StreamingStage | 3d web streaming stage}  to use for the stream.
   */
  streamingStage?: StreamingStage;

  /**
   * Enabled features
   */
  features?: DeckFeatureFlags;

  /**
   * Environment IDs that are whitelisted for the current brand
   * If this is provided, the user will be able to select environments from the gallery.
   * Also, this configuration property cancels out {@link DeckProperties | dayEnvironment} and  {@link DeckProperties | nightEnvironment} environments.
   */
  environments?: Array<EnvironmentId>;

  /**
   * Providing a day and a night environment will show a button that toggles between the two environments.
   */
  dayEnvironment?: EnvironmentId;

  /**
   * Providing a day and a night environment will show a button that toggles between the two environments.
   */
  nightEnvironment?: EnvironmentId;

  /**
   * Default environment that loads with the app.
   * If not provided, the first environment from {@link DeckProperties | environments} will be used.
   * Alternatively, {@link DeckProperties | dayEnvironment} is used if it is provided.
   * Finally, if none provided, the AVE default environment is loaded.
   */
  initialEnvironment?: EnvironmentId;

  /**
   * Exterior cameras that users can use.
   */
  exteriorCameras?: Array<CameraId>;

  /**
   * Interior camera is used with the camera switching button.
   */
  interiorCameras?: Array<CameraId>;

  /**
   * Default camera that loads with the app.
   * If not provided, the first camera from {@link DeckProperties | cameras} will be used.
   * Alternatively, {@link DeckProperties | exteriorCamera} is used if it is provided.
   * Finally, if none provided, the AVE default camera is loaded.
   */
  initialCamera?: CameraId;

  /**
   * Defines the camera flight to be used for the "play" button
   */
  cameraFlight?: string;

  /**
   * Defines if the camera flight should be started automatically on stream start
   */
  cameraFlightOnStart?: boolean;

  /**
   * Defines if the animation spots should be enabled automatically on stream start
   */
  enableAnimationSpots?: boolean;

  /**
   * The duration in seconds that the session remains active with no inputs from the user
   */
  inactivityTimeout?: number;

  /**
   * The duration in seconds that the session remains active after the last client disconnects
   */
  reconnectGracePeriod?: number;

  /**
   * Design template. Audi defaults to NGC, and others use the default design.
   * @see {@link DeckTemplate}
   */
  template?: DeckTemplate;
}

/**
 * Available templates
 */
export enum DeckTemplate {
  "default" = "Default template with square white buttons",
  "ngc" = "Next Generation Car template",
  "vw" = "Volkswagen template with round buttons",
  "cupra-exhibition" = "3D exhibition template for cupra brand",
  "vw-exhibition" = "3D exhibition template for vw brand"
}

/**
 * Feature flags that can be used to enable/disable certain features
 * @see {@link DeckFeature}
 */
export type DeckFeatureFlags = Partial<{
  [featureKey in keyof typeof DeckFeature]: boolean;
}>;

/**
 * Supported features that can be enabled or disabled
 */
export enum DeckFeature {
  "animations" = "Animation selection",
  "close" = "Close",
  "zoom" = "Zoom in/out",
  "cameraFlight" = "Camera flight",
  "cameraNavigation" = "Camera navigation buttons",
  "environments" = "Environment selection",
  "interior" = "Interior toggle",
  "animationSpots" = "Animation spots",
  "fullscreen" = "Fullscreen toggle"
}

export enum Brand {
  "Audi" = "A",
  "Volkswagen" = "V",
  "VolkswagenNutzfahrzeuge" = "N",
  "Cupra" = "S",
  "Seat" = "S",
  "Lamborghini" = "L",
  "Bentley" = "E",
  "MAN" = "M"
}
